import { useState } from 'react';
import {
  DragStartEvent,
  DragEndEvent,
  DragOverEvent,
  UniqueIdentifier,
} from '@dnd-kit/core';
import { WorkItem } from '../types';

export const useDragAndDrop = (
  data: WorkItem[],
  categories: string[],
  setCategories: (categories: string[]) => void,
  onUpdate: (item: WorkItem) => void,
  viewMode?: 'tree' | 'table',
  setData?: (data: WorkItem[]) => void
) => {
  const [activeId, setActiveId] = useState<UniqueIdentifier | null>(null);

  const getItemType = (id: string): 'category' | 'subcategory' | 'item' => {
    if (id.startsWith('level1-')) return 'category';
    if (id.startsWith('level2::')) return 'subcategory';
    return 'item';
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event: DragOverEvent) => {
    const { over } = event;
    
    if (!over) {
      return;
    }
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    
    if (!over || active.id === over.id) {
      setActiveId(null);
      return;
    }
    
    
    // Tree view logic (existing)
    const activeType = getItemType(active.id as string);
    const overType = getItemType(over.id as string);
    
    // Handle reordering categories
    if (activeType === 'category' && overType === 'category') {
      const activeCategory = (active.id as string).replace('level1-', '');
      const overCategory = (over.id as string).replace('level1-', '');
      
      if (activeCategory !== overCategory) {
        // Reorder categories in localStorage
        const savedCategories = localStorage.getItem('cleanRoomCategories');
        if (savedCategories) {
          try {
            const parsed = JSON.parse(savedCategories);
            const activeIndex = parsed.indexOf(activeCategory);
            const overIndex = parsed.indexOf(overCategory);
            
            if (activeIndex > -1 && overIndex > -1) {
              // Remove the active category
              const [removed] = parsed.splice(activeIndex, 1);
              // Insert it at the new position
              parsed.splice(overIndex, 0, removed);
              
              localStorage.setItem('cleanRoomCategories', JSON.stringify(parsed));
              setCategories(parsed);
            }
          } catch (e) {
            console.error('Failed to reorder categories');
          }
        }
      }
    }
    // Handle moving items to different categories/subcategories
    else if (activeType === 'item') {
      const item = data.find(i => i.id === active.id);
      if (item) {
        let targetLevel1 = item.level1;
        let targetLevel2 = item.level2;
        let shouldUpdate = false;
        
        if (overType === 'category') {
          // Dropped on a category - move to that category's Items
          targetLevel1 = (over.id as string).replace('level1-', '');
          targetLevel2 = 'Items';
          shouldUpdate = true;
        } else if (overType === 'subcategory') {
          // Dropped on a subcategory - move to that subcategory
          const parts = (over.id as string).replace('level2::', '').split('::');
          targetLevel1 = parts[0];
          targetLevel2 = parts[1] || 'Items';
          shouldUpdate = true;
        } else if (overType === 'item') {
          // Dropped on another item - move to same category/subcategory
          const targetItem = data.find(i => i.id === over.id);
          if (targetItem) {
            targetLevel1 = targetItem.level1;
            targetLevel2 = targetItem.level2;
            shouldUpdate = (targetLevel1 !== item.level1 || targetLevel2 !== item.level2);
          }
        }
        
        // Only update if the category or subcategory changed
        if (shouldUpdate) {
          const updatedItem = { 
            ...item, 
            level1: targetLevel1, 
            level2: targetLevel2,
            metadata: {
              ...item.metadata,
              modified: new Date(),
            }
          };
          onUpdate(updatedItem);
        }
      }
    }
    
    setActiveId(null);
  };

  const getActiveItem = () => {
    if (!activeId) return null;
    
    const type = getItemType(activeId as string);
    
    if (type === 'category') {
      return {
        type: 'category',
        name: (activeId as string).replace('level1-', ''),
      };
    } else if (type === 'subcategory') {
      const parts = (activeId as string).replace('level2::', '').split('::');
      return {
        type: 'subcategory',
        name: parts[1] || 'Items',
      };
    } else {
      const item = data.find(i => i.id === activeId);
      return item ? { type: 'item', ...item } : null;
    }
  };

  return {
    activeId,
    handleDragStart,
    handleDragOver,
    handleDragEnd,
    getActiveItem,
    getItemType,
  };
};